import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * Reduces the large Printify media set to the relevant selected-color group
 * before Horizon initializes the gallery.
 *
 * This helper is intentionally limited to gallery content. PDP page layout,
 * sticky positioning, and accordion placement are defined in Liquid/JSON.
 *
 * @param {Element} gallery
 * @returns {Element}
 */
function pruneBrightMeadowsGallery(gallery) {
  const pruner = window.BMDPruneProductGallery;
  if (typeof pruner === 'function') pruner(gallery);
  return gallery;
}

const diagnosticsEnabled = new URLSearchParams(window.location.search).get('bmd_debug') === '1';
let diagnosticsInitialized = false;

/**
 * Finds the nearest ancestor that actually provides vertical scrolling.
 * Horizon uses .page-wrapper as the desktop page scroller.
 *
 * @param {Element|null} element
 * @returns {Element|null}
 */
function nearestVerticalScroller(element) {
  let current = element?.parentElement ?? null;

  while (current) {
    const style = getComputedStyle(current);
    const overflowY = style.overflowY;
    const canScroll = (overflowY === 'auto' || overflowY === 'scroll') && current.scrollHeight > current.clientHeight + 1;

    if (canScroll) return current;
    current = current.parentElement;
  }

  return document.scrollingElement;
}

/**
 * @param {Element|null} element
 * @returns {string}
 */
function describeElement(element) {
  if (!element) return 'missing';
  if (element === document.scrollingElement) return 'document.scrollingElement';
  if (element.id) return `#${element.id}`;
  const classes = [...element.classList].slice(0, 3);
  return `${element.tagName.toLowerCase()}${classes.length ? `.${classes.join('.')}` : ''}`;
}

/**
 * @param {Element|null} element
 * @returns {string}
 */
function computedPosition(element) {
  return element ? getComputedStyle(element).position : 'missing';
}

/**
 * Captures the invariants that must remain true for this PDP.
 *
 * @returns {object}
 */
function diagnosticSnapshot() {
  const productInformation = document.querySelector('[data-testid="product-information"]');
  const productGrid = productInformation?.querySelector('product-component[data-product-grid-content]') ?? null;
  const media = productInformation?.querySelector('.product-information__media') ?? null;
  const gallery = productInformation?.querySelector('media-gallery') ?? null;
  const details = productInformation?.querySelector('.product-details') ?? null;
  const detailsGroup = details?.querySelector(':scope > .group-block') ?? null;
  const accordion = productInformation?.querySelector('.accordion--accordion_Fp2LqW') ?? null;
  const pageWrapper = document.querySelector('.page-wrapper');
  const desktop = window.matchMedia('(min-width: 750px)').matches;

  const mediaRect = media?.getBoundingClientRect() ?? null;
  const detailsRect = details?.getBoundingClientRect() ?? null;
  const gridRect = productGrid?.getBoundingClientRect() ?? null;
  const accordionRect = accordion?.getBoundingClientRect() ?? null;

  const mediaScroller = nearestVerticalScroller(media);
  const detailsScroller = nearestVerticalScroller(details);
  const sameScroller = Boolean(mediaScroller && detailsScroller && mediaScroller === detailsScroller);
  const expectedScroller = !desktop || !pageWrapper || mediaScroller === pageWrapper || mediaScroller === document.scrollingElement;

  const galleryPosition = computedPosition(gallery);
  const detailsPosition = computedPosition(details);
  const detailsGroupPosition = computedPosition(detailsGroup);
  const noStickyColumns =
    !['sticky', 'fixed'].includes(galleryPosition) &&
    !['sticky', 'fixed'].includes(detailsPosition) &&
    !['sticky', 'fixed'].includes(detailsGroupPosition);

  const accordionOutsideTopGrid = Boolean(accordion && productGrid && !productGrid.contains(accordion));

  let columnsBalanced = true;
  let widthDifferencePercent = 0;
  if (desktop && mediaRect && detailsRect) {
    const maxWidth = Math.max(mediaRect.width, detailsRect.width, 1);
    widthDifferencePercent = Math.abs(mediaRect.width - detailsRect.width) / maxWidth * 100;
    columnsBalanced = widthDifferencePercent <= 8;
  }

  let accordionGap = null;
  let noSyntheticGap = true;
  if (gridRect && accordionRect) {
    accordionGap = Math.round((accordionRect.top - gridRect.bottom) * 10) / 10;
    noSyntheticGap = accordionGap <= 48;
  }

  return {
    timestamp: new Date().toISOString(),
    viewport: `${window.innerWidth}x${window.innerHeight}`,
    desktop,
    invariants: {
      noStickyColumns,
      sameVerticalScroller: sameScroller,
      expectedPageScroller: expectedScroller,
      accordionOutsideTopGrid,
      columnsBalanced,
      noSyntheticGap,
    },
    metrics: {
      galleryPosition,
      detailsPosition,
      detailsGroupPosition,
      mediaScroller: describeElement(mediaScroller),
      detailsScroller: describeElement(detailsScroller),
      mediaWidth: mediaRect ? Math.round(mediaRect.width * 10) / 10 : null,
      detailsWidth: detailsRect ? Math.round(detailsRect.width * 10) / 10 : null,
      widthDifferencePercent: Math.round(widthDifferencePercent * 10) / 10,
      accordionGap,
      accordionParent: describeElement(accordion?.parentElement ?? null),
      accordionInsideProductGrid: Boolean(accordion && productGrid?.contains(accordion)),
    },
  };
}

/**
 * Creates a small read-only diagnostics panel. It never alters PDP layout.
 *
 * @returns {HTMLElement}
 */
function ensureDiagnosticsPanel() {
  let panel = document.getElementById('bmd-pdp-diagnostics');
  if (panel) return panel;

  panel = document.createElement('aside');
  panel.id = 'bmd-pdp-diagnostics';
  panel.setAttribute('aria-live', 'polite');
  panel.style.cssText = [
    'position:fixed',
    'right:12px',
    'bottom:12px',
    'z-index:2147483647',
    'width:min(390px,calc(100vw - 24px))',
    'max-height:70vh',
    'overflow:auto',
    'padding:12px',
    'border:1px solid currentColor',
    'background:#fff',
    'color:#111',
    'font:12px/1.4 ui-monospace,SFMono-Regular,Menlo,monospace',
    'box-shadow:0 8px 28px rgba(0,0,0,.18)',
  ].join(';');

  document.body.append(panel);
  return panel;
}

/**
 * @param {object} snapshot
 * @param {{pass:boolean, message:string}|null} scrollCoupling
 */
function renderDiagnostics(snapshot, scrollCoupling = null) {
  const panel = ensureDiagnosticsPanel();
  const rows = Object.entries(snapshot.invariants).map(([name, pass]) => `${pass ? 'PASS' : 'FAIL'}  ${name}`);
  if (scrollCoupling) rows.push(`${scrollCoupling.pass ? 'PASS' : 'FAIL'}  scrollCoupling — ${scrollCoupling.message}`);

  const metrics = Object.entries(snapshot.metrics).map(([name, value]) => `${name}: ${String(value)}`);

  panel.textContent = [
    'BMD PDP LAYOUT DIAGNOSTICS',
    `viewport: ${snapshot.viewport}`,
    '',
    ...rows,
    '',
    ...metrics,
  ].join('\n');

  panel.style.whiteSpace = 'pre-wrap';
}

/**
 * Initializes passive regression monitoring. It observes rerenders and scroll
 * behavior but never mutates the product layout.
 */
function initPDPDiagnostics() {
  if (!diagnosticsEnabled || diagnosticsInitialized) return;
  diagnosticsInitialized = true;

  let scheduled = false;
  let scrollCoupling = null;
  let lastScrollSample = null;

  const run = () => {
    scheduled = false;
    const snapshot = diagnosticSnapshot();
    renderDiagnostics(snapshot, scrollCoupling);
    return snapshot;
  };

  const schedule = () => {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(run);
  };

  const productInformation = document.querySelector('[data-testid="product-information"]');
  if (productInformation) {
    const observer = new MutationObserver(() => {
      lastScrollSample = null;
      schedule();
    });
    observer.observe(productInformation, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['class', 'style'],
    });
  }

  document.addEventListener(
    'scroll',
    () => {
      requestAnimationFrame(() => {
        const productInformationNow = document.querySelector('[data-testid="product-information"]');
        const gallery = productInformationNow?.querySelector('media-gallery');
        const details = productInformationNow?.querySelector('.product-details');
        if (!gallery || !details) return;

        const sample = {
          galleryTop: gallery.getBoundingClientRect().top,
          detailsTop: details.getBoundingClientRect().top,
        };

        if (lastScrollSample) {
          const galleryMove = sample.galleryTop - lastScrollSample.galleryTop;
          const detailsMove = sample.detailsTop - lastScrollSample.detailsTop;
          const movement = Math.max(Math.abs(galleryMove), Math.abs(detailsMove));

          if (movement > 1) {
            const mismatch = Math.abs(galleryMove - detailsMove);
            scrollCoupling = {
              pass: mismatch <= 2,
              message: `gallery Δ${galleryMove.toFixed(1)}px / details Δ${detailsMove.toFixed(1)}px`,
            };
          }
        }

        lastScrollSample = sample;
        schedule();
      });
    },
    true
  );

  window.addEventListener('resize', () => {
    lastScrollSample = null;
    schedule();
  });

  document.addEventListener('shopify:section:load', schedule);
  document.addEventListener('change', schedule, true);

  window.BMDPDPDiagnostics = {
    snapshot: diagnosticSnapshot,
    run,
  };

  run();
}

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    pruneBrightMeadowsGallery(this);
    super.connectedCallback();
    initPDPDiagnostics();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        pruneBrightMeadowsGallery(newMediaGallery);
        this.replaceWith(newMediaGallery);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom media selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
